/**
 * @fileoverview Core - Feature Usage Tracking
 * @module features/core/track-feature
 * 
 * @description
 * Track feature usage events to understand how users
 * interact with different parts of your product.
 * 
 * @useCases
 * - Track feature adoption rates
 * - Measure feature engagement
 * - Identify power users
 * - Prioritize feature development
 * - Detect underused features
 * 
 * @metrics
 * - Feature adoption rate
 * - Feature usage frequency
 * - Users per feature
 * - Feature stickiness
 * - Time spent per feature
 * 
 * @chartData
 * - Bar chart: Most used features
 * - Heatmap: Feature usage by user segment
 * - Line chart: Feature adoption over time
 * - Table: Feature engagement metrics
 * 
 * @example
 * ```typescript
 * const trackFeature = new TrackFeature(webId, endpoint, apiKey);
 * 
 * await trackFeature.create({
 *   userId: 'user_123',
 *   action: 'export_report',
 *   properties: { featureName: 'export_report', featureId: 'export_report_001' }
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { TimeSeriesData } from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================


type FeatureConfigProperties = {
  featureId?: string;
  featureName: string;
  metadata?: Record<string, unknown>;
};

/**
 * Base configuration for feature tracking.
 */
export interface FeatureConfig {
  /** Feature tracking properties */
  action: string;

  /** Feature category */
  category?: string;

  userId: string;

  /** Event properties */
  properties: FeatureConfigProperties;

}

/**
 * Detailed feature usage configuration.
 */
export interface FeatureUsageConfig {
  /** Feature identifier */
  featureId: string;
  /** Feature name */
  featureName: string;
  /** User ID */
  userId?: string;
  /** Usage action */
  action: 'viewed' | 'used' | 'completed' | 'abandoned';
  /** Time spent (ms) */
  duration?: number;
  /** Success/failure */
  success?: boolean;
  /** Feature category */
  category?: string;
  /**
   * Wire event name to emit. Defaults to `feature_usage`. The `.track()`
   * router passes the lifecycle-specific name here (`feature_viewed`,
   * `feature_completed`, etc.) so consumers can filter on a distinct
   * `event_name`; `action`/`subtype` still carry the lifecycle stage.
   */
  eventName?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Feature discovery configuration.
 */
export interface FeatureDiscoveryConfig {
  /** Feature identifier */
  featureId: string;
  /** User ID */
  userId: string;
  /** How feature was discovered */
  discoveryMethod: 'organic' | 'tooltip' | 'announcement' | 'search' | 'onboarding' | 'recommendation' | string;
  /** Whether user engaged with feature */
  engaged: boolean;
}

/**
 * Feature tracking response.
 */
export interface FeatureResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    tracked: boolean;
    featureId?: string;
    isFirstUse?: boolean;
  };
  timestamp: string;
}

/**
 * Feature analytics data.
 */
export interface FeatureAnalyticsData {
  /** Summary metrics */
  summary: {
    totalFeatures: number;
    totalUsage: number;
    uniqueUsers: number;
    avgUsagePerUser: number;
    mostUsedFeature: string;
    leastUsedFeature: string;
  };
  /** Usage by feature */
  byFeature: Array<{
    featureId: string;
    featureName: string;
    category: string;
    usageCount: number;
    uniqueUsers: number;
    adoptionRate: number;
    avgDuration: number;
    successRate: number;
  }>;
  /** Usage by user segment */
  bySegment: Array<{
    segment: string;
    topFeatures: string[];
    avgFeaturesUsed: number;
  }>;
  /** Feature trends */
  trends: TimeSeriesData;
  /** Feature correlations */
  correlations: Array<{
    featureA: string;
    featureB: string;
    correlation: number;
  }>;
}

// ============================================================================
// TRACK FEATURE CLASS
// ============================================================================

/**
 * TrackFeature - Track feature usage events.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides feature tracking including:
 * - Generic feature events
 * - Detailed usage tracking
 * - Feature discovery tracking
 * 
 * @example
 * ```typescript
 * const trackFeature = new TrackFeature(webId, endpoint, apiKey);
 * 
 * // Simple feature tracking
 * await trackFeature.create({
 *   userId: 'user_123',
 *   action: 'dark_mode_enabled',
 *   properties: { featureName: 'dark_mode' }
 * });
 * 
 * // Detailed feature usage
 * await trackFeature.trackUsage({
 *   featureId: 'export',
 *   featureName: 'Data Export',
 *   userId: 'user_123',
 *   action: 'completed',
 *   duration: 5000,
 *   success: true,
 *   properties: { format: 'csv' }
 * });
 * ```
 */

export class TrackFeature extends FlowGridTransport {
  /**
   * Records a feature usage event.
   * 
   * @param config - Feature configuration
   * @returns Promise resolving to feature response
   */
  async create(config: FeatureConfig): Promise<FeatureResponse> {
    return await this.init("create_tracking", {
      type: "feature_usage",
      subtype: "create_custom",
      userId: config.userId,
      category: config.category,
      action: config.action,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks detailed feature usage.
   * 
   * @param config - Feature usage configuration
   * @returns Promise resolving to feature response
   */
  async trackUsage(config: FeatureUsageConfig): Promise<FeatureResponse> {
    return await this.init(config.eventName ?? "feature_usage", {
      type: "feature_usage",
      subtype: config.action,
      featureId: config.featureId,
      featureName: config.featureName,
      userId: config.userId,
      action: config.action,
      duration: config.duration,
      success: config.success,
      category: config.category,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

}

export default TrackFeature;
